/**
 * Derived-metrics helpers for BrokerAccount.
 *
 * Everything here is computed on-the-fly from the existing account
 * fields (twrCurve, spyCurve, cashflows, …). Keeps mock-data.ts
 * schemaless for these values so when we cut over to a real API, we
 * just swap the source of the raw series and this file keeps working.
 *
 * UNIT CONVENTION — all `*Pct` fields on `DerivedAccountMetrics` are
 * PERCENT-SCALE (12.5 = +12.5 %). This matches the convention already
 * used for `BrokerAccount.risk.apyPct`, `twrCurve`, etc. Cards that
 * render these values should use `formatWeeklyPct` (which treats its
 * input as already-percent), NOT `formatPct` (which multiplies by
 * 100 — it expects decimals).
 *
 * The one exception — `stocksDayChangePct` on the account itself —
 * is DECIMAL, matching the chart's emitted `pctChange` convention.
 * We don't surface a day-change field from here; MetricsPanel
 * computes that locally using `windowReturnDecimal` from chart-math.
 *
 * All cumulative-return math (windowed TWR, SPY, All-time) routes
 * through `src/lib/chart-math.ts` so the formulas stay in one place
 * and the chart + cards can't drift apart.
 */
import type { BrokerAccount } from "@/data/mock-data";
import {
  windowReturnDecimal,
  rebaseCumulativePct,
  windowDollarReturn,
  findCurveIdxOnOrAfter,
  daysBackIso,
  jan1IsoForAsOfDate
} from "@/lib/chart-math";

export interface DerivedAccountMetrics {
  /** Last ~7-day TWR, PERCENT-SCALE. */
  weeklyTwrPct: number;
  /** Dollar effective change this week — cashflow-neutral, matches
   *  the chart's hero `usdChange` formula over the same window. */
  weeklyTwrDollar: number;
  /** SPY price-only return over the ~7-day window, PERCENT-SCALE. */
  weeklyTwrSpyPct: number;

  /** Money-Weighted APY — we surface TWR-APY (`risk.apyPct`) until the
   *  real IRR is available, PERCENT-SCALE. */
  weightedApyPct: number;
  /** SPY annualised return over the full window, PERCENT-SCALE. */
  weightedApySpyPct: number;

  /** Year-to-date TWR (PERCENT-SCALE), anchored at Jan 1 of the
   *  account's last-sample year. */
  ytdPct: number;
  /** SPY year-to-date over the same window, PERCENT-SCALE. */
  ytdSpyPct: number;
  /** YTD dollar return — cashflow-neutral, same formula as the chart's
   *  hero on YTD range. */
  ytdDollar: number;

  /** Past-month TWR (PERCENT-SCALE) over a trailing 30 calendar days —
   *  matches the chart's 1M range window so the card and the 1M chip
   *  agree. */
  pastMonthPct: number;
  /** SPY over the trailing 30-calendar-day window, PERCENT-SCALE. */
  pastMonthSpyPct: number;
  /** Past-month dollar return — cashflow-neutral, chart's hero on 1M. */
  pastMonthDollar: number;

  /** All-time TWR (PERCENT-SCALE) — matches the chart's ALL-range
   *  Portfolio chip reading. */
  allTimeTwrPct: number;
  /** All-time TWR dollars — cashflow-neutral "returns only" delta:
   *  `equity[last] − equity[0] − post-opening cashflows`. Matches the
   *  chart's hero `usdChange` on ALL range. Distinct from
   *  `account.allTimeChange` (unrealised P&L on currently-held stocks)
   *  which can flip sign on a winning account if capital was withdrawn. */
  allTimeTwrDollar: number;
  /** SPY over the same all-time window, PERCENT-SCALE. */
  allTimeSpyPct: number;

  /** Net cash in (deposits − withdrawals) since inception, signed USD. */
  netDeposits: number;
  /** Total count of cashflow transactions (deposits + withdrawals). */
  netDepositsCount: number;
  /** Count of deposit events (`cf.type === "deposit"`). */
  depositsCount: number;
  /** Count of withdrawal events (`cf.type === "withdrawal"`). */
  withdrawalsCount: number;
}

/**
 * Convert a DECIMAL return (0.1316) to PERCENT-SCALE (13.16) rounded
 * to 3 decimal places for display stability.
 */
function toPct(dec: number): number {
  return +(dec * 100).toFixed(3);
}

// ---- Range-scoped metrics (Sergey 2026-04-30) -----------------------------

export interface RangeScopedMetrics {
  /** Number of calendar days the active window spans (end − start). */
  windowDays: number;
  /** ISO yyyy-mm-dd of the window's first day (echoed for display). */
  startIso: string;
  /** ISO yyyy-mm-dd of the window's last day (echoed for display). */
  endIso: string;
  /** Cashflow-neutral % return over the window, PERCENT-SCALE. */
  rangeReturnPct: number;
  /** SPY price-only % return over the same window, PERCENT-SCALE. */
  rangeReturnSpyPct: number;
  /** Cashflow-neutral $ return over the window. */
  rangeReturnDollar: number;
  /** Annualised TWR over the window, PERCENT-SCALE. `null` when the
   *  window is shorter than {@link MIN_APY_DAYS} — annualising a
   *  noisy short window inflates volatility-driven returns into
   *  meaningless headline APYs (Sergey 2026-04-30: "Do not show APY
   *  if range <3 months"). */
  rangeApyPct: number | null;
  /** SPY annualised over the same window. `null` under the same gate. */
  rangeApySpyPct: number | null;
  /** Peak-to-trough drawdown on the rebased TWR slice, PERCENT-SCALE
   *  (negative). `0` when the window has fewer than 2 sample points. */
  rangeMaxDDPct: number;
  /** SPY peak-to-trough drawdown over the same window, PERCENT-SCALE. */
  rangeMaxDDSpyPct: number;
  /** Max recovery time ("MRec") — the longest stretch, in CALENDAR
   *  DAYS, that the curve stayed below a prior peak inside the window.
   *  An underwater period still open at the window's end counts (it's
   *  the one that matters most), so this can be "and counting".
   *  `0` when the curve never dipped below a peak. */
  rangeMaxRecoveryDays: number;
  /** True when the longest underwater stretch is still unresolved at
   *  the end of the window — i.e. the account has not yet made a new
   *  high. Lets the UI render "90d+ (ongoing)". */
  rangeMaxRecoveryOngoing: boolean;
  /** SPY's max recovery time over the same window, in calendar days. */
  rangeMaxRecoverySpyDays: number;
  /** Sharpe over the window — weekly returns of the rebased TWR /
   *  stdev × √52 with risk-free 4.5%. `null` when the window has
   *  fewer than 2 weeks of data. */
  rangeSharpe: number | null;
  /** SPY's Sharpe over the same window. `null` under the same gate. */
  rangeSharpeSpy: number | null;
  /** YTD TWR anchored at Jan 1 of the WINDOW-END year → window-end,
   *  PERCENT-SCALE. So the YTD card is window-end-aware (Sergey
   *  2026-04-30: "show YTD to actual range date") — moving the
   *  end forward changes YTD; moving only the start does not. */
  rangeYtdPct: number;
  /** SPY YTD over the same window-end-anchored span. */
  rangeYtdSpyPct: number;
  /** YTD dollar return over the same span — cashflow-neutral. */
  rangeYtdDollar: number;
  /** Year used for the YTD label ("YTD 2026"). */
  rangeYtdYear: number;
}

/** Annualised metrics need a minimum window length to be meaningful.
 *  90 days = roughly one quarter; below that the multiplier blows up
 *  short-term volatility into double-digit APYs that misrepresent the
 *  underlying return. Sergey 2026-04-30. */
export const MIN_APY_DAYS = 90;

/**
 * Compute a range-scoped metric bundle from the account's curves and
 * the chart's active window. `startIso`/`endIso` should match the
 * chart's `onRangeChange` payload — the helper resolves them to
 * curve-index slices using the same `findCurveIdxOnOrAfter` rule the
 * chart uses internally.
 *
 * Returns `null` when the inputs aren't usable yet (empty curve, empty
 * date strings) so callers can render a placeholder instead of a
 * misleading $0 / 0% block.
 */
/**
 * Max recovery time ("MRec") — the longest stretch spent BELOW a prior
 * peak, in calendar days.
 *
 * `cum` is a cumulative-return series (percent scale); `dates` are the
 * matching ISO dates. Elapsed time is measured from the dates, not the
 * sample count, so weekends/holidays/data gaps count as the real
 * waiting time a user would have experienced.
 *
 * A stretch still unresolved at the end of the series is included and
 * reported via `ongoing` — an open drawdown is the one that matters
 * most, so excluding it would flatter the result.
 */
export function maxRecoveryTime(
  cum: readonly number[],
  dates: readonly string[]
): { days: number; ongoing: boolean } {
  if (cum.length < 2) return { days: 0, ongoing: false };
  const dayGap = (i: number, j: number): number => {
    const from = dates[i];
    const to = dates[j];
    if (!from || !to) return 0;
    const ms = new Date(to).getTime() - new Date(from).getTime();
    return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 86400000)) : 0;
  };
  let peak = cum[0]!;
  let peakIdx = 0;
  let best = 0;
  let bestOngoing = false;
  let underwater = false;
  for (let i = 1; i < cum.length; i++) {
    const v = cum[i]!;
    if (v >= peak) {
      // New high — closes any open underwater stretch.
      if (underwater) {
        const len = dayGap(peakIdx, i);
        if (len > best) { best = len; bestOngoing = false; }
        underwater = false;
      }
      peak = v;
      peakIdx = i;
    } else {
      underwater = true;
    }
  }
  // Still below the last peak when the series ended.
  if (underwater) {
    const len = dayGap(peakIdx, cum.length - 1);
    if (len >= best) { best = len; bestOngoing = true; }
  }
  return { days: best, ongoing: bestOngoing };
}

export function deriveRangeScopedMetrics(
  a: BrokerAccount,
  startIso: string,
  endIso: string
): RangeScopedMetrics | null {
  const twr = a.twrCurve;
  const spy = a.spyCurve;
  if (twr.length === 0 || spy.length === 0) return null;
  if (!startIso || !endIso) return null;

  // Snap start to the first curve date >= startIso. End is the last
  // curve date <= endIso (or the very last point if endIso is past
  // today's data). Mirrors the chart's `sliceByRange` resolution.
  const startIdx = findCurveIdxOnOrAfter(a.curveDates, startIso);
  let endIdx = a.curveDates.length - 1;
  for (let i = a.curveDates.length - 1; i >= 0; i--) {
    if ((a.curveDates[i] ?? "") <= endIso) {
      endIdx = i;
      break;
    }
  }
  if (endIdx <= startIdx) return null;

  const sliceLen = endIdx - startIdx + 1;
  const startDate = a.curveDates[startIdx]!;
  const endDate = a.curveDates[endIdx]!;
  const windowDays = Math.max(
    1,
    Math.round(
      (new Date(endDate).getTime() - new Date(startDate).getTime()) /
        (24 * 60 * 60 * 1000)
    )
  );

  // ---- Range Return (cashflow-neutral) ----------------------------------
  const rangeReturnPct = toPct(windowReturnDecimal(twr, startIdx, endIdx));
  const rangeReturnSpyPct = toPct(windowReturnDecimal(spy, startIdx, endIdx));
  const rangeReturnDollar = windowDollarReturn(
    a.equityCurve,
    a.curveDates,
    a.cashflows,
    startIdx,
    endIdx
  );

  // ---- Annualised — gated to MIN_APY_DAYS -------------------------------
  let rangeApyPct: number | null = null;
  let rangeApySpyPct: number | null = null;
  if (windowDays >= MIN_APY_DAYS) {
    const portfolioRet = windowReturnDecimal(twr, startIdx, endIdx);
    const spyRet = windowReturnDecimal(spy, startIdx, endIdx);
    // Snap a ~1-year window to exactly 365 days (Sergey 2026-08-03).
    // Picking "1Y" slices from the first trading day on/after the
    // boundary, so the real window is usually 363-364 days. Annualising
    // that to 365 nudged the APY a tenth of a point above the Range
    // Return (30.64 % → 30.74 %), which reads as a bug on a range the
    // user explicitly chose as "one year". Within 3 days of a year the
    // two cards should simply agree; every shorter range still
    // annualises for real, which is the whole point of the card.
    const annualDays =
      Math.abs(windowDays - 365) <= 3 ? 365 : windowDays;
    const annPort = (Math.pow(1 + portfolioRet, 365 / annualDays) - 1) * 100;
    const annSpy = (Math.pow(1 + spyRet, 365 / annualDays) - 1) * 100;
    rangeApyPct = +annPort.toFixed(3);
    rangeApySpyPct = +annSpy.toFixed(3);
  }

  // ---- Max Drawdown — rebase the slice to start at 0% --------------------
  // Geometric rebase (Sergey 2026-08-03): these slices are cumulative
  // returns since inception, so re-anchoring them by subtraction
  // distorts the growth index `100 + c` that `maxDD` walks — and by
  // extension the Sharpe slices below, which reuse the same arrays.
  const portRebased = rebaseCumulativePct(twr.slice(startIdx, endIdx + 1));
  const spyRebased = rebaseCumulativePct(spy.slice(startIdx, endIdx + 1));
  const maxDD = (cum: number[]): number => {
    if (cum.length < 2) return 0;
    let peak = -Infinity;
    let mdd = 0;
    for (const c of cum) {
      const v = 100 + c;
      if (v > peak) peak = v;
      const dd = ((v - peak) / peak) * 100;
      if (dd < mdd) mdd = dd;
    }
    return +mdd.toFixed(3);
  };
  const rangeMaxDDPct = maxDD(portRebased);
  const rangeMaxDDSpyPct = maxDD(spyRebased);

  // ---- Max recovery time (MRec) ------------------------------------------
  // Longest run of consecutive days spent BELOW a prior peak — the
  // worst "underwater" stretch. Measured in calendar days between the
  // curve dates so gaps (weekends, holidays, missing samples) count as
  // real elapsed time, which is what a user waiting to get back to
  // even actually experiences.
  //
  // A stretch that hasn't recovered by the window's end is included
  // and flagged `ongoing` — an unresolved drawdown is the most
  // important one, so dropping it would flatter the number.
  const windowDates = a.curveDates.slice(startIdx, endIdx + 1);
  const portRecovery = maxRecoveryTime(portRebased, windowDates);
  const spyRecovery = maxRecoveryTime(spyRebased, windowDates);
  const rangeMaxRecoveryDays = portRecovery.days;
  const rangeMaxRecoveryOngoing = portRecovery.ongoing;
  const rangeMaxRecoverySpyDays = spyRecovery.days;

  // ---- Sharpe — weekly returns of the rebased slices ---------------------
  // Same `step = 7` weekly sampling the lifetime Sharpe uses
  // (`buildRiskDataForTWR`). Risk-free 4.5% APY. Need ≥2 weekly
  // samples to compute a stdev — gate at sliceLen >= ~14 days
  // (2 weekly samples).
  let rangeSharpe: number | null = null;
  let rangeSharpeSpy: number | null = null;
  if (sliceLen >= 15) {
    const weeklyRets = (cum: number[]): number[] => {
      const r: number[] = [];
      const step = 7;
      for (let i = step; i < cum.length; i += step) {
        const p = 100 + cum[i - step]!;
        const c = 100 + cum[i]!;
        if (p !== 0) r.push(((c - p) / p) * 100);
      }
      return r;
    };
    const stdev = (arr: number[]): number => {
      if (arr.length < 2) return 0;
      const m = arr.reduce((s, v) => s + v, 0) / arr.length;
      return Math.sqrt(
        arr.reduce((s, v) => s + (v - m) ** 2, 0) / (arr.length - 1)
      );
    };
    const ann = (r: number): number =>
      windowDays === 0 ? 0 : (Math.pow(1 + r / 100, 365 / windowDays) - 1) * 100;

    const pRets = weeklyRets(portRebased);
    const sRets = weeklyRets(spyRebased);
    const pStd = stdev(pRets);
    const sStd = stdev(sRets);
    const wpy = 52;
    const volPct = pStd * Math.sqrt(wpy);
    const volSpyPct = sStd * Math.sqrt(wpy);
    const portRangeRet =
      portRebased[portRebased.length - 1] ?? 0;
    const spyRangeRet =
      spyRebased[spyRebased.length - 1] ?? 0;
    const apyPort = ann(portRangeRet);
    const apySpy = ann(spyRangeRet);
    const rf = 4.5;
    rangeSharpe = volPct > 0 ? +((apyPort - rf) / volPct).toFixed(3) : 0;
    rangeSharpeSpy = volSpyPct > 0 ? +((apySpy - rf) / volSpyPct).toFixed(3) : 0;
  }

  // ---- YTD anchored at Jan 1 of WINDOW-END's year ------------------------
  // Per Sergey 2026-04-30: "show YTD to actual range date, so it will
  // show same number if end date is the same and start after year
  // start". So Jan 1 → endIdx, ignoring startIdx entirely.
  const jan1Iso = jan1IsoForAsOfDate(endDate);
  const ytdIdx = findCurveIdxOnOrAfter(a.curveDates, jan1Iso);
  const rangeYtdPct = toPct(windowReturnDecimal(twr, ytdIdx, endIdx));
  const rangeYtdSpyPct = toPct(windowReturnDecimal(spy, ytdIdx, endIdx));
  const rangeYtdDollar = windowDollarReturn(
    a.equityCurve,
    a.curveDates,
    a.cashflows,
    ytdIdx,
    endIdx
  );
  const rangeYtdYear = new Date(endDate).getFullYear();

  return {
    windowDays,
    startIso: startDate,
    endIso: endDate,
    rangeReturnPct,
    rangeReturnSpyPct,
    rangeReturnDollar,
    rangeApyPct,
    rangeApySpyPct,
    rangeMaxDDPct,
    rangeMaxDDSpyPct,
    rangeMaxRecoveryDays,
    rangeMaxRecoveryOngoing,
    rangeMaxRecoverySpyDays,
    rangeSharpe,
    rangeSharpeSpy,
    rangeYtdPct,
    rangeYtdSpyPct,
    rangeYtdDollar,
    rangeYtdYear
  };
}

/**
 * Compute the full metric bundle for an account. Calendar-date
 * boundaries (not data-point-count) so real accounts whose curves are
 * trading-day-filtered get the same window as the chart.
 */
export function deriveAccountMetrics(a: BrokerAccount): DerivedAccountMetrics {
  const twr = a.twrCurve;
  const spy = a.spyCurve;
  const last = twr.length - 1;
  const lastDateIso = a.curveDates[last] ?? new Date().toISOString().slice(0, 10);

  // ---- Weekly window — 7 CALENDAR days back ------------------------------
  const weekAgoIso = daysBackIso(lastDateIso, 7);
  const wkStart = findCurveIdxOnOrAfter(a.curveDates, weekAgoIso);
  const weeklyTwrPct = toPct(windowReturnDecimal(twr, wkStart, last));
  const weeklyTwrSpyPct = toPct(windowReturnDecimal(spy, wkStart, last));
  const weeklyTwrDollar = windowDollarReturn(
    a.equityCurve,
    a.curveDates,
    a.cashflows,
    wkStart,
    last
  );

  // ---- YTD window (first curveDate in the account's as-of year) ----------
  const jan1Iso = jan1IsoForAsOfDate(lastDateIso);
  const ytdIdx = findCurveIdxOnOrAfter(a.curveDates, jan1Iso);
  const ytdPct = toPct(windowReturnDecimal(twr, ytdIdx, last));
  const ytdSpyPct = toPct(windowReturnDecimal(spy, ytdIdx, last));
  const ytdDollar = windowDollarReturn(
    a.equityCurve,
    a.curveDates,
    a.cashflows,
    ytdIdx,
    last
  );

  // ---- Past-month window — 30 CALENDAR days back -------------------------
  const monthAgoIso = daysBackIso(lastDateIso, 30);
  const pmStart = findCurveIdxOnOrAfter(a.curveDates, monthAgoIso);
  const pastMonthPct = toPct(windowReturnDecimal(twr, pmStart, last));
  const pastMonthSpyPct = toPct(windowReturnDecimal(spy, pmStart, last));
  const pastMonthDollar = windowDollarReturn(
    a.equityCurve,
    a.curveDates,
    a.cashflows,
    pmStart,
    last
  );

  // ---- All-time window — inception → today ------------------------------
  const allTimeTwrPct = toPct(windowReturnDecimal(twr, 0, last));
  const allTimeSpyPct = toPct(windowReturnDecimal(spy, 0, last));
  const allTimeTwrDollar = windowDollarReturn(
    a.equityCurve,
    a.curveDates,
    a.cashflows,
    0,
    last
  );

  // ---- Net Deposits ------------------------------------------------------
  const netDeposits = a.cashflows.reduce((sum, cf) => sum + cf.amount, 0);
  const depositsCount = a.cashflows.filter((cf) => cf.type === "deposit").length;
  const withdrawalsCount = a.cashflows.filter((cf) => cf.type === "withdrawal").length;

  return {
    weeklyTwrPct,
    weeklyTwrDollar,
    weeklyTwrSpyPct,
    weightedApyPct: a.risk.apyPct,
    weightedApySpyPct: a.risk.apySpyPct,
    ytdPct,
    ytdSpyPct,
    ytdDollar,
    pastMonthPct,
    pastMonthSpyPct,
    pastMonthDollar,
    allTimeTwrPct,
    allTimeTwrDollar,
    allTimeSpyPct,
    netDeposits,
    netDepositsCount: a.cashflows.length,
    depositsCount,
    withdrawalsCount
  };
}

/**
 * Format a percent-scale number with a directional triangle and fixed
 * decimals. Sergey 2026-04-30: ± replaced with ▲ / ▼ so the metric
 * cards consistently use directional glyphs instead of mixed
 * sign-prefix conventions. Zero stays unmarked. Negative percentages
 * render as `▼ 1.23%` (positive value with leading triangle) — color
 * is applied by the caller's CSS so the triangle stays
 * sign-consistent regardless of theme.
 */
export function formatWeeklyPct(v: number, digits = 2): string {
  const abs = Math.abs(v).toFixed(digits);
  if (v > 0) return `▲ ${abs}%`;
  if (v < 0) return `▼ ${abs}%`;
  return `${abs}%`;
}
